import { SupabaseService } from './SupabaseService';

/**
 * FileUploadService - Service for uploading driver documents to Supabase Storage
 */
export class FileUploadService {
  private static readonly BUCKET_NAME = 'imagedriver';

  /**
   * Upload a single file to driver's folder
   */
  static async uploadFile(
    file: File, 
    phoneNumber: string, 
    folder: string
  ): Promise<string | null> {
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const filePath = `${phoneNumber}/${folder}/${fileName}`;

      const { data, error } = await SupabaseService.uploadFile(
        this.BUCKET_NAME,
        filePath,
        file,
        {
          contentType: file.type,
          cacheControl: '3600',
          upsert: false
        }
      );

      if (error) {
        console.error('Error uploading file:', error);
        return null;
      }

      if (data) {
        return SupabaseService.getPublicUrl(this.BUCKET_NAME, filePath);
      }

      return null;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  }

  /**
   * Upload multiple files to driver's folder
   */
  static async uploadMultipleFiles(
    files: File[], 
    phoneNumber: string, 
    folder: string
  ): Promise<string[]> {
    const uploadPromises = files.map(file => 
      this.uploadFile(file, phoneNumber, folder)
    );

    const results = await Promise.all(uploadPromises);
    return results.filter(url => url !== null) as string[];
  }

  /**
   * Delete file from storage
   */
  static async deleteFile(filePath: string): Promise<boolean> {
    try {
      // Extract file path from public URL if needed
      const actualPath = filePath.includes('imagedriver/') 
        ? filePath.split('imagedriver/')[1] 
        : filePath;

      const { error } = await SupabaseService.deleteFile(
        this.BUCKET_NAME,
        actualPath
      );

      if (error) {
        console.error('Error deleting file:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      return false;
    }
  }

  /**
   * Get folder name for specific document type
   */
  static getFolderForDocumentType(documentType: string): string {
    const folderMap: { [key: string]: string } = {
      // Avatar
      'url_avatar': 'avatars',
      
      // Motorcycle documents
      'motorcycle_license_url': 'motorcycle_license',
      'motorcycle_front_image': 'motorcycle_images',
      'motorcycle_rear_image': 'motorcycle_images',
      
      // Car documents
      'car_license_url': 'car_license',
      'car_front_image': 'car_images',
      'car_rear_image': 'car_images',
      'car_front_seat_image': 'car_images',
      'car_rear_seat_image': 'car_images',
      
      // Identity and residence documents
      'cccd_images': 'cccd',
      'residence_confirmation_url': 'residence',
      
      // Vehicle documents
      'vehicle_registration_urls': 'vehicle_registration',
      'inspection_certificate_url': 'inspection_certificates',
      'vehicle_badge_url': 'vehicle_badges',
      'reflective_sticker_url': 'reflective_stickers',
      'insurance_certificate_url': 'insurance_certificates',
      
      // Health and legal documents
      'health_certificate_urls': 'health_certificates',
      'drug_test_urls': 'drug_tests',
      'criminal_record_urls': 'criminal_records',
      
      // Other documents
      'commitment_video_url': 'commitment_videos',
      'cooperative_member_urls': 'cooperative_member',
      'signature_url': 'signatures'
    };

    return folderMap[documentType] || 'other';
  }

  /**
   * Upload driver document with automatic folder assignment
   */
  static async uploadDriverDocument(
    file: File,
    phoneNumber: string,
    documentType: string
  ): Promise<string | null> {
    const folder = this.getFolderForDocumentType(documentType);
    return this.uploadFile(file, phoneNumber, folder);
  }

  /**
   * Upload multiple driver documents
   */
  static async uploadMultipleDriverDocuments(
    files: File[],
    phoneNumber: string,
    documentType: string
  ): Promise<string[]> {
    const folder = this.getFolderForDocumentType(documentType);
    return this.uploadMultipleFiles(files, phoneNumber, folder);
  }
}
